import { useMutation } from '@tanstack/react-query'
import { configureAuth } from 'react-query-auth'
import { Navigate } from 'react-router-dom'
import z from 'zod'

import { CLIENT_ID, NAV_LINKS } from '~/constants'

import { api } from './api-client'
import { getAccessToken, setTokens } from './auth-tokens'

import { AuthResponse, User } from '~/types/api'

const getUser = async (): Promise<User | null> => {
  const token = getAccessToken()
  if (!token) {
    return null
  }
  try {
    const response = await api.get('/oauth/userinfo')
    return response.data
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('getUser failed:', err)
    return null
  }
}

const logout = (): Promise<void> => {
  return api.post('/auth/logout')
}

export const loginInputSchema = z.object({
  email: z.string().min(1, 'Required').email('Invalid email'),
  password: z.string().min(5, 'Required'),
  recaptcha: z.string().min(1, 'Required'),
})

export type LoginInput = z.infer<typeof loginInputSchema>

const loginWithEmailAndPassword = (data: LoginInput): Promise<AuthResponse> => {
  const [user, pass, recaptcha] = [data.email, data.password, data.recaptcha].map(encodeURIComponent)
  const AUTH_CLIENT_ID = CLIENT_ID
  const url = `grant_type=password&username=${user}&password=${pass}&client_id=${AUTH_CLIENT_ID}&scope=openid profile&g-recaptcha-response-data=${recaptcha}`
  return api.post('/oauth/token?' + url)
}

export const useSSOLogin = () => {
  const ssoLogin = async (email: string): Promise<{ url: string }> => {
    const res = await api.post<{ url: string }>(`/rh/sso_informations`, { email: email })
    return res.data
  }
  return useMutation<{ url: string }, Error, string>({
    mutationKey: ['sso_login'],
    mutationFn: ssoLogin,
  })
}

const authConfig = {
  userFn: getUser,
  loginFn: async (data: LoginInput) => {
    const response = await loginWithEmailAndPassword(data)
    setTokens(response.data.access_token, response.data.refresh_token)
    const user = await getUser()
    return user
  },
  registerFn: async () => {
    // implement registration to return a User | null
    // placeholder for now:
    return null
  },
  logoutFn: logout,
}

export const { useUser, useLogin, useLogout, AuthLoader } = configureAuth(authConfig)

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useUser()

  if (user.isLoading) {
    return null
  }

  if (!user.data) {
    return <Navigate to={NAV_LINKS.SIGNIN.to} replace />
  }

  return <>{children}</>
}
